import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormCheckbox, FormGroup, FormRadioGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { mapRefDataPickList } from 'shared/utils/Helpers';

export const LiftDesignDetails = React.forwardRef((props, ref) => {
  const { value = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;
  const {
    driveSuspensionType: savedDriveSuspensionType = '',
    otherDriveSuspensionType: savedOtherDriveSuspensionType = '',
    isMRLLift: savedIsMRLLift = 'no',
    carriageType: savedCarriageType = '',
    controlType: savedControlType = '',
    maxSpeed: savedMaxSpeed = '',
    operatingParams: savedOperatingParams = '',
    maxRatedLoad: savedMaxRatedLoad = '',
    maxPassengers: savedMaxPassengers = '',
    maxTravel: savedMaxTravel = '',
    forConstruction: savedForConstruction = '',
    inUncompliedCondition: savedInUncompliedCondition = '',
    additionalControlMeasure: savedAdditionalControlMeasure = '',
    hasBeenInspected: savedHasBeenInspected = '',
  } = value;

  const [driveSuspensionType, setDriveSuspensionType] = useState(savedDriveSuspensionType);
  const [otherDriveSuspensionType, setOtherDriveSuspensionType] = useState(savedOtherDriveSuspensionType);
  const [isMRLLift, setIsMRLLift] = useState(savedIsMRLLift);
  const [carriageType, setCarriageType] = useState(savedCarriageType);
  const [controlType, setControlType] = useState(savedControlType);
  const [maxSpeed, setMaxSpeed] = useState(savedMaxSpeed);
  const [maxRatedLoad, setMaxRatedLoad] = useState(savedMaxRatedLoad);
  const [maxPassengers, setMaxPassengers] = useState(savedMaxPassengers);
  const [maxTravel, setMaxTravel] = useState(savedMaxTravel);

  const REF_CONSTANTS = PlantDesignRefData.lift;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.lift;

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const suspensionTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.suspensionTypeOptions]);
  const carriageTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.carriageTypeOptions]);
  const controlTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.controlTypeOptions]);

  const validators = [
    {
      fieldName: 'driveSuspensionType',
      fieldValue: driveSuspensionType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'driveSuspensionType_other',
      fieldValue: otherDriveSuspensionType,
      isValid: (v) => !isEmpty(v) || driveSuspensionType !== 'Other (please describe)',
      errorMessage: () => 'Please enter details',
    },

    {
      fieldName: 'carriageType',
      fieldValue: carriageType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'controlType',
      fieldValue: controlType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'maxSpeed',
      fieldValue: maxSpeed,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },

    {
      fieldName: 'maxRatedLoad',
      fieldValue: maxRatedLoad,
      isValid: (v) => isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxPassengers',
      fieldValue: maxPassengers,
      isValid: (v) => isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxTravel',
      fieldValue: maxTravel,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let details = {
      driveSuspensionType,
      otherDriveSuspensionType,
      isMRLLift,
      carriageType,
      controlType,
      maxSpeed,
      maxRatedLoad,
      maxPassengers,
      maxTravel,
    };
    onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    driveSuspensionType,
    otherDriveSuspensionType,
    isMRLLift,
    carriageType,
    controlType,
    maxSpeed,
    maxRatedLoad,
    maxPassengers,
    maxTravel,
  ]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <RadioOrDropdownSelector
        id="driveSuspensionType"
        name="driveSuspensionType"
        options={suspensionTypeOptions}
        title={PAGE_CONSTANTS.driveSuspensionType.label}
        errorMessage={errors?.driveSuspensionType?.errorMessage}
        hasError={showErrors && errors?.driveSuspensionType?.invalid}
        onChange={(v) => {
          setDriveSuspensionType(v);
          setOtherDriveSuspensionType('');
        }}
        value={driveSuspensionType}
        showExtraInput={driveSuspensionType === 'Other (please describe)'}
        onChangeExtraInput={setOtherDriveSuspensionType}
        extraInputValue={otherDriveSuspensionType}
        extraInputErrorMessage={errors?.driveSuspensionType_other?.errorMessage}
        extraInputHasError={showErrors && errors?.driveSuspensionType_other?.invalid}
        extraInputTitle="Other please describe"
        extraInputType="textarea"
        margin={{ top: 'xl' }}
      />

      <div className={`${styles.labelMsg} ${styles.marginTopTwoHalfRem}`}>{PAGE_CONSTANTS.isMRLLift.label}</div>
      <div className={styles.helpMsg}>
        Machine room less lifts are a type of either traction or hydraulic lift which do not require a machine room for
        the lift.
      </div>
      <FormCheckbox
        label="Yes, it is a Machine-Room-Less (MRL) Lift"
        onChange={() => {
          //togle value
          setIsMRLLift(isMRLLift === 'yes' ? 'no' : 'yes');
        }}
        checked={isMRLLift === 'yes'}
        name="mrlLift"
        value="true"
      />

      <FormRadioGroup
        id="carriageType"
        name="carriageType"
        vertical={true}
        options={carriageTypeOptions}
        onChange={setCarriageType}
        value={carriageType}
        errorMessage={errors?.carriageType?.errorMessage}
        hasError={showErrors && errors?.carriageType?.invalid}
        legend={PAGE_CONSTANTS.carriageType.label}
        margin={{ top: 52 }}
      />

      <FormRadioGroup
        id="controlType"
        name="controlType"
        vertical={true}
        options={controlTypeOptions}
        onChange={setControlType}
        value={controlType}
        errorMessage={errors?.controlType?.errorMessage}
        hasError={showErrors && errors?.controlType?.invalid}
        legend={PAGE_CONSTANTS.controlType.label}
        margin={{ top: 52 }}
      />

      <FormGroup
        id="maxRatedLoad"
        label={PAGE_CONSTANTS.maxRatedLoad.label}
        isRequired
        margin={{ top: 52 }}
        errorMessage={errors?.maxRatedLoad?.errorMessage}
        hasError={showErrors && errors?.maxRatedLoad?.invalid}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxRatedLoad.suffix}
          onChange={(e) => {
            setMaxRatedLoad(e.target.value);
          }}
          value={maxRatedLoad}
        />
      </FormGroup>

      <FormGroup
        id="maxPassengers"
        label={PAGE_CONSTANTS.maxPassengers.label}
        isRequired
        errorMessage={errors?.maxPassengers?.errorMessage}
        hasError={showErrors && errors?.maxPassengers?.invalid}
        margin={{ top: 'md' }}
      >
        <Input
          onChange={(e) => {
            setMaxPassengers(e.target.value);
          }}
          value={maxPassengers}
          inputWidth="xs"
        />
      </FormGroup>

      <FormGroup
        id="maxTravel"
        label={PAGE_CONSTANTS.maxTravel.label}
        isRequired
        errorMessage={errors?.maxTravel?.errorMessage}
        hasError={showErrors && errors?.maxTravel?.invalid}
        margin={{ top: 'md' }}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxTravel.suffix}
          onChange={(e) => {
            setMaxTravel(e.target.value);
          }}
          value={maxTravel}
        />
      </FormGroup>

      <FormGroup
        id="maxSpeed"
        label={PAGE_CONSTANTS.maxSpeed.label}
        isRequired
        margin={{ top: 'md' }}
        errorMessage={errors?.maxSpeed?.errorMessage}
        hasError={showErrors && errors?.maxSpeed?.invalid}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxSpeed.suffix}
          onChange={(e) => {
            setMaxSpeed(e.target.value);
          }}
          value={maxSpeed}
        />
      </FormGroup>
    </>
  );
});

LiftDesignDetails.displayName = 'LiftDesignDetails';

LiftDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
